import { formatCurrencyNoDecimals, formatPercentage } from '@if/core/utils'
import { FormSubmitButton, StepLinearProgress, useFormContainerContext } from '@if/mui'
import { Card, CardActions, CardContent, CardHeader, Divider, Stack, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomBuydownFormValues } from '../customBuydown-schema'

// The contribution slider tops out at 30%; the bar fills across that range.
const APPROVAL_MAX_CONTRIBUTION = 30

// Thresholds (in contribution % points) shared by the text label and the bar color.
const approvalSteps = [
  { from: 0, color: 'error' as const },
  { from: 5, color: 'warning' as const },
  { from: 10, color: 'info' as const },
  { from: 15, color: 'success' as const },
]

const approvalLikelihood = (contribution: number): 'low' | 'moderate' | 'good' | 'high' => {
  if (contribution < 0.05) return 'low'
  if (contribution < 0.1) return 'moderate'
  if (contribution < 0.15) return 'good'
  return 'high'
}

export interface PlanSummaryCardProps {
  adminFeeRate: number
  locked?: boolean
}

export const PlanSummaryCard = ({ adminFeeRate, locked = false }: PlanSummaryCardProps) => {
  const { t } = useTranslation()
  const { methods } = useFormContainerContext<CustomBuydownFormValues>()
  const contribution = useWatch({ control: methods.control, name: 'contribution' }) ?? 0
  const maxFinancingAmount = useWatch({ control: methods.control, name: 'maxFinancingAmount' })

  const planCode = `FLASH${Math.round(contribution * 10000)}/${Math.round(adminFeeRate * 100)}`
  const likelihood = approvalLikelihood(contribution)

  return (
    <Card>
      <CardHeader title={t('customBuydown.summaryTitle')} />
      <CardContent>
        <Stack spacing={2} divider={<Divider />}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.planCode')}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {planCode}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.yourFeeRate')}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatPercentage(contribution * 100)} %
            </Typography>
          </Stack>
          {maxFinancingAmount != null && maxFinancingAmount > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('customBuydown.maxCost')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrencyNoDecimals(contribution * maxFinancingAmount)}
              </Typography>
            </Stack>
          )}
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.approvalLikelihood')}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {t(`customBuydown.approval.${likelihood}`)}
            </Typography>
            <StepLinearProgress
              steps={approvalSteps}
              value={contribution * 100}
              max={APPROVAL_MAX_CONTRIBUTION}
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t(`customBuydown.approvalHint.${likelihood}`)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
      <CardActions>
        {locked ? (
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 1 }}>
            {t('customBuydown.inherited.setByBanner')}
          </Typography>
        ) : (
          <FormSubmitButton fullWidth label={t('customBuydown.saveAndApply')} />
        )}
      </CardActions>
    </Card>
  )
}
